import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Scale } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan');
  React.useEffect(() => { useAuthStore.setState({ error: null }); }, []);
  const { register, loading, error } = useAuthStore();
  const [form, setForm] = useState({
    firm_name: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
  });
  const [localError, setLocalError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (fieldErrors[e.target.name]) {
      setFieldErrors({ ...fieldErrors, [e.target.name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setFieldErrors({});

    const errors = {};
    if (!form.firm_name.trim()) errors.firm_name = 'Firm name is required';
    else if (form.firm_name.trim().length > 30) errors.firm_name = 'Firm name must be 30 characters or fewer';
    if (!form.first_name.trim()) errors.first_name = 'First name is required';
    if (!form.last_name.trim()) errors.last_name = 'Last name is required';
    if (!form.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (!form.password) {
      errors.password = 'Password is required';
    } else if (form.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    if (!form.confirm_password) {
      errors.confirm_password = 'Please confirm your password';
    } else if (form.password && form.password !== form.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    try {
      await register({
        firm_name: form.firm_name.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        password: form.password,
        plan: selectedPlan || 'free',
      });
      toast.success('Welcome to Sixty10! You\'re on the Free plan.');
      navigate('/');
    } catch (err) {
      setLocalError(err.message);
    }
  };

  const inputClass = (name) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
      fieldErrors[name] ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  const planLabel = selectedPlan && selectedPlan !== 'free'
    ? selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-2">
            <Scale className="h-10 w-10 text-brand-600" />
            <span className="text-3xl font-bold text-gray-900">Sixty10</span>
          </div>
          <p className="text-gray-500">Start free. No credit card required.</p>
        </div>

        {/* Selected plan note */}
        {planLabel && (
          <div className="bg-brand-50 border border-brand-200 text-brand-700 text-sm rounded-lg px-4 py-3 mb-4 text-center">
            Selected plan: <strong>{planLabel}</strong> &mdash; you can change this anytime
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-4">
          {(localError || error) && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              {localError || error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Firm Name</label>
            <input
              type="text"
              name="firm_name"
              value={form.firm_name}
              onChange={handleChange}
              maxLength={30}
              className={inputClass('firm_name')}
              placeholder="Smith & Associates"
            />
            {fieldErrors.firm_name && <p className="text-red-500 text-xs mt-1">{fieldErrors.firm_name}</p>}
            <p className="text-xs text-gray-400 mt-1">{form.firm_name.length}/30</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                className={inputClass('first_name')}
              />
              {fieldErrors.first_name && <p className="text-red-500 text-xs mt-1">{fieldErrors.first_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                className={inputClass('last_name')}
              />
              {fieldErrors.last_name && <p className="text-red-500 text-xs mt-1">{fieldErrors.last_name}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className={inputClass('email')}
              placeholder="you@yourfirm.com"
            />
            {fieldErrors.email && <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              className={inputClass('password')}
              placeholder="Min. 8 characters"
            />
            {fieldErrors.password && <p className="text-red-500 text-xs mt-1">{fieldErrors.password}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              type="password"
              name="confirm_password"
              value={form.confirm_password}
              onChange={handleChange}
              className={inputClass('confirm_password')}
            />
            {fieldErrors.confirm_password && <p className="text-red-500 text-xs mt-1">{fieldErrors.confirm_password}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Creating account...' : 'Create Free Account'}
          </button>

          <p className="text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
